/**
 * Alpine Store: Cart + Discount Projections v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * IIFE registering `$store.cart` and `$store.discount` as reactive READ-ONLY
 * projections of `<cart-engine>` state, plus `Alpine.magic('cart')` for action
 * access in templates.
 *
 * AUTHOR SURFACE
 * ─────────────────────────────────────────────────────────────────────────────
 *   Reads (reactive):
 *     $store.cart.item_count        — number
 *     $store.cart.total_price       — cents
 *     $store.cart.items             — array
 *     $store.cart.isEmpty           — boolean
 *     $store.cart.itemLoading       — { [key]: true }
 *     $store.cart.hasItem(id)       — boolean
 *     $store.cart.quantityOf(id)    — number
 *     ...
 *     $store.discount.discount_codes — array
 *     $store.discount.appliedDiscounts — filtered array
 *     $store.discount.hasDiscounts  — boolean
 *
 *   Actions (delegated to <cart-engine>):
 *     $cart.add({ id, mode })       — Promise
 *     $cart.changeItem({ key, quantity })
 *     $cart.removeItem(key)
 *     $cart.undoRemove()
 *     $cart.applyDiscount(code)
 *     $cart.removeDiscount(code)
 *     ...
 *
 * `$cart` resolves to `window.cartEngine` at access time, so it stays correct
 * across engine re-mounts (rare, but section-morph could detach/reattach the
 * engine element).
 *
 * LOAD ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * Must run AFTER `logger.js`, `events.js`, AFTER `alpine-store-ui.js` (for
 * deterministic alpine:init handler ordering), and BEFORE Alpine.js core
 * fires `alpine:init`. Loaded via `<script defer>` in scripts.liquid.
 *
 * Subscribes to `cart:update` so the projection re-syncs whenever the engine
 * mutates state. The engine emits an `action:'init'` update in
 * connectedCallback so the first paint reflects SSR.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };

  const log = Logger.ns('store.cart');

  const formatMoney = (cents) =>
    (window.Theme && typeof window.Theme.formatMoney === 'function')
      ? window.Theme.formatMoney(cents)
      : '';

  /**
   * SSR seed — the engine hasn't connected yet when this IIFE runs (it's an
   * ES module that loads after defer scripts). We seed from window.Theme.cartData
   * so the first paint of reactive bindings shows the correct cart count.
   */
  const seedFromSSR = () => {
    const c = window.Theme?.cartData || {};
    return {
      items: c.items || [],
      item_count: c.item_count || 0,
      total_price: c.total_price || 0,
      total_discount: c.total_discount || 0,
      discount_codes: c.discount_codes || [],
    };
  };

  /**
   * Pull a snapshot from <cart-engine> if it's connected, otherwise return
   * the SSR seed. Called on every cart:update so the projection mirrors the
   * canonical state.
   */
  const snapshotEngineState = () => {
    const engine = window.cartEngine;
    if (engine && typeof engine.getState === 'function') {
      return engine.getState();
    }
    return seedFromSSR();
  };

  const setupStores = () => {
    if (typeof Alpine === 'undefined') {
      log.error('Alpine not loaded — cannot register projections');
      return;
    }

    const seed = seedFromSSR();

    // ─── $money magic (kept here for parity with the old store) ───────────

    Alpine.magic('money', () => (cents) => formatMoney(cents));

    // ─── $cart magic — actions go through the engine ──────────────────────
    //
    // Returns `window.cartEngine` so callers get the live API. We don't
    // wrap or proxy — the engine *is* the action surface. Templates use:
    //
    //   @click="$cart.add({ id, mode: 'silent' })"
    //   :disabled="$cart === null"  ← rarely needed; engine is always present
    //
    // If the engine isn't connected yet, this returns null and callers see
    // a TypeError — which is the correct failure mode (it points the dev at
    // the real bug: `<cart-engine>` missing from theme.liquid).
    Alpine.magic('cart', () => window.cartEngine);
    // Mirror for ergonomics: $discount.X() also routes through the engine.
    // Discount actions live on the engine too (applyDiscount/removeDiscount).
    Alpine.magic('discount', () => window.cartEngine);

    // ─── $store.cart — reactive read projection ───────────────────────────

    Alpine.store('cart', {
      // State (overwritten on every cart:update)
      items: seed.items,
      item_count: seed.item_count,
      total_price: seed.total_price,
      total_discount: seed.total_discount,
      discount_codes: seed.discount_codes,

      // UI state (projected from engine)
      loading: false,
      itemLoading: {},
      error: null,
      removedItem: null,

      // ─── Derived getters ─────────────────────────────────────────────────

      get isEmpty() {
        return this.item_count === 0;
      },
      get hasItems() {
        return this.item_count > 0;
      },
      get formattedTotal() {
        return formatMoney(this.total_price);
      },
      get formattedDiscount() {
        return this.total_discount > 0 ? formatMoney(this.total_discount) : null;
      },
      get isAnyItemLoading() {
        return Object.keys(this.itemLoading).length > 0;
      },

      // ─── Helpers (read-only) ─────────────────────────────────────────────

      isItemLoading(key) {
        return this.itemLoading[key] === true;
      },

      formatMoney(cents) {
        return formatMoney(cents);
      },

      // Item search helpers — same semantics as cart-engine reads, but reads
      // the projected items so they're reactive in Alpine effects/x-show.
      findItem(productId) {
        if (productId == null) return null;
        const idNum = Number(productId);
        return this.items.find(
          (i) => Number(i.product_id) === idNum || Number(i.variant_id) === idNum,
        ) || null;
      },
      hasItem(productId) {
        return !!this.findItem(productId);
      },
      quantityOf(productId) {
        const item = this.findItem(productId);
        return item ? item.quantity : 0;
      },

      // ─── Sync from engine (called by cart:update listener) ───────────────
      //
      // Not a "method", but exposed so the IIFE listener can call it. It
      // copies every field from the engine's state — Alpine sees the
      // assignments and re-runs effects.
      _syncFromEngine(state) {
        if (!state) return;
        this.items = state.items || [];
        this.item_count = state.item_count || 0;
        this.total_price = state.total_price || 0;
        this.total_discount = state.total_discount || 0;
        this.discount_codes = state.discount_codes || [];
        this.loading = !!state.loading;
        this.itemLoading = state.itemLoading || {};
        this.error = state.error || null;
        this.removedItem = state.removedItem || null;
      },
    });

    // ─── $store.discount — reactive read projection ───────────────────────

    Alpine.store('discount', {
      discount_codes: seed.discount_codes,
      discountLoading: false,
      discountError: null,

      get appliedDiscounts() {
        return (this.discount_codes || []).filter((dc) => dc.applicable !== false);
      },
      get hasDiscounts() {
        return this.appliedDiscounts.length > 0;
      },

      // Tests / templates expecting an action API on the store get a clear
      // failure: there are no apply/remove methods here. Use `$cart.applyDiscount`.

      _syncFromEngine(state) {
        if (!state) return;
        this.discount_codes = state.discount_codes || [];
        this.discountLoading = !!state.discountLoading;
        this.discountError = state.discountError || null;
      },

      // Tests still call `$store.discount.clearError()` in a few places —
      // kept here because it mutates the projected state only (no engine
      // call). The engine has its own `clearDiscountError` that emits an
      // update so other surfaces see the cleared error.
      clearError() {
        this.discountError = null;
        if (window.cartEngine && typeof window.cartEngine.clearDiscountError === 'function') {
          window.cartEngine.clearDiscountError();
        }
      },
    });

    // ─── Subscribe to cart:update — keep projections in sync ──────────────

    const eventName = window.Theme?.events?.NAMES?.cartUpdate || 'cart:update';
    document.addEventListener(eventName, () => {
      const state = snapshotEngineState();
      // Alpine.store(...) returns a reactive proxy; assignments tick effects.
      Alpine.store('cart')._syncFromEngine(state);
      Alpine.store('discount')._syncFromEngine(state);
    });

    // If the engine already connected before alpine:init (unlikely with defer
    // ordering, but possible), do a one-shot sync so the projection is correct
    // on first paint.
    if (window.cartEngine && typeof window.cartEngine.getState === 'function') {
      const state = window.cartEngine.getState();
      Alpine.store('cart')._syncFromEngine(state);
      Alpine.store('discount')._syncFromEngine(state);
    }

    log.info('Alpine cart projections registered');
  };

  document.addEventListener('alpine:init', setupStores);

  log.info('alpine-store-cart v1.0.0 loaded');
})();
